import { useEffect, useState } from 'react';
import { getAdminVenueData, savePubInfo, saveQAItem, deleteQAItem, bulkImportQA, saveRoom, deleteRoom, saveVenueTable, deleteVenueTable, GetAdminVenueDataOutputType } from 'zite-endpoints-sdk';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Building2, HelpCircle, Table2, Upload, X, CheckCircle2, Layout, Flame, FlaskConical } from 'lucide-react';
import VenuePubInfoForm from './VenuePubInfoForm';
import VenueRoomsTab from './VenueRoomsTab';
import VenueTablesTab from './VenueTablesTab';
import TablePlannerCanvas from './TablePlannerCanvas';
import FireSafetyAdminTab from './FireSafetyAdminTab';
import CoshhAdminTab from './CoshhAdminTab';

type VenueData = GetAdminVenueDataOutputType;
type QAItem = VenueData['qaItems'][0];

function QAManageTab({ items: initItems, onRefresh }: { items: QAItem[]; onRefresh: () => void }) {
  const [items, setItems] = useState(initItems);
  const [editing, setEditing] = useState<Partial<QAItem> | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [replaceAll, setReplaceAll] = useState(false);

  useEffect(() => { setItems(initItems); }, [initItems]);

  const filtered = items.filter(q =>
    !search || q.question.toLowerCase().includes(search.toLowerCase()) || q.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async () => {
    if (!editing?.question?.trim() || !editing?.answer?.trim()) { toast.error('Question and answer are required'); return; }
    setSaving(true);
    try {
      await saveQAItem({ question: editing.question!, answer: editing.answer!, category: editing.category || 'General', active: editing.active ?? true, displayOrder: editing.displayOrder, id: editing.id });
      toast.success(editing.id ? 'Updated' : 'Added');
      setEditing(null);
      onRefresh();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteQAItem({ id }); toast.success('Deleted'); onRefresh(); }
    catch (e: any) { toast.error(e.message); }
  };

  const handleImport = async () => {
    if (!importText.trim()) { toast.error('Please paste some Q&A content first'); return; }
    setImporting(true);
    try {
      const res = await bulkImportQA({ text: importText, format: 'auto', replaceAll });
      toast.success(`Imported ${res.imported} items`);
      setShowImport(false);
      setImportText('');
      onRefresh();
    } catch (e: any) { toast.error(e.message); }
    finally { setImporting(false); }
  };

  const categories = [...new Set(items.map(q => q.category))].sort();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Q&A..." className="pl-9" />
          <HelpCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} className="gap-1.5"><Upload className="w-3.5 h-3.5" />Bulk Import</Button>
          <Button size="sm" onClick={() => setEditing({ question: '', answer: '', category: 'General', active: true })} className="gap-1.5"><Plus className="w-3.5 h-3.5" />Add Q&A</Button>
        </div>
      </div>

      {showImport && (
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-foreground text-sm">Bulk Import Q&amp;A</p>
            <button onClick={() => setShowImport(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
          </div>
          <p className="text-xs text-muted-foreground">Paste your Q&amp;A list in any format. Supported: <code className="bg-muted px-1 rounded">Category|Question|Answer</code> (one per line), CSV, or freeform text (AI parses it).</p>
          <Textarea value={importText} onChange={e => setImportText(e.target.value)} rows={8} className="font-mono text-xs" />
          <div className="flex items-center gap-3">
            <Switch id="replace" checked={replaceAll} onCheckedChange={setReplaceAll} />
            <label htmlFor="replace" className="text-xs text-muted-foreground cursor-pointer">Replace all existing Q&amp;A items</label>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleImport} disabled={importing} size="sm" className="gap-1.5">
              {importing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Importing...</> : <><CheckCircle2 className="w-3.5 h-3.5" />Import</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowImport(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {editing && (
        <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
          <p className="font-semibold text-foreground text-sm">{editing.id ? 'Edit Q&A' : 'Add Q&A'}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Category *</label>
              <Input list="qa-categories" value={editing.category || ''} onChange={e => setEditing(s => ({ ...s!, category: e.target.value }))} placeholder="e.g. General" />
              <datalist id="qa-categories">
                {categories.map(c => <option key={c} value={c} />)}
                <option value="General" /><option value="Food & Drink" /><option value="Events" /><option value="Location" />
              </datalist>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch id="qa-active" checked={editing.active ?? true} onCheckedChange={v => setEditing(s => ({ ...s!, active: v }))} />
              <label htmlFor="qa-active" className="text-xs text-muted-foreground">Active</label>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Question *</label>
            <Input value={editing.question || ''} onChange={e => setEditing(s => ({ ...s!, question: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Answer *</label>
            <Textarea value={editing.answer || ''} onChange={e => setEditing(s => ({ ...s!, answer: e.target.value }))} rows={3} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}</Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No Q&amp;A items yet.</p>
        ) : (
          filtered.map(item => (
            <div key={item.id} className={`border rounded-lg p-3 flex items-start gap-3 ${!item.active ? 'opacity-50' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs text-primary font-medium bg-primary/10 rounded px-1.5 py-0.5">{item.category}</span>
                  {!item.active && <span className="text-xs text-muted-foreground">(hidden)</span>}
                </div>
                <p className="text-sm font-medium text-foreground">{item.question}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.answer}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(item)}><Pencil className="w-3.5 h-3.5" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this Q&amp;A item?</AlertDialogTitle></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>
      <p className="text-xs text-muted-foreground">{items.length} Q&amp;A items total</p>
    </div>
  );
}

export default function VenueAdminTab() {
  const [data, setData] = useState<VenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('pubinfo');

  const refresh = () => {
    getAdminVenueData({}).then(setData).catch(() => toast.error('Failed to reload'));
  };

  useEffect(() => {
    getAdminVenueData({}).then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  const rooms = (data?.rooms ?? []).map(r => ({ id: r.id, name: r.name }));

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Building2 className="w-4 h-4 text-primary" />
        <h3 className="font-semibold text-foreground">Venue Management</h3>
        <span className="text-xs text-muted-foreground">— Pub info, Q&amp;A, rooms, tables, fire safety &amp; COSHH</span>
      </div>
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="mb-5 flex-wrap h-auto gap-1">
          <TabsTrigger value="pubinfo"><Building2 className="w-3.5 h-3.5 mr-1" />Pub Info</TabsTrigger>
          <TabsTrigger value="qa"><HelpCircle className="w-3.5 h-3.5 mr-1" />Q&amp;A ({data?.qaItems.length ?? 0})</TabsTrigger>
          <TabsTrigger value="rooms">Rooms ({data?.rooms.length ?? 0})</TabsTrigger>
          <TabsTrigger value="tables"><Table2 className="w-3.5 h-3.5 mr-1" />Tables ({data?.tables.length ?? 0})</TabsTrigger>
          <TabsTrigger value="planner"><Layout className="w-3.5 h-3.5 mr-1" />Table Planner</TabsTrigger>
          <TabsTrigger value="firesafety"><Flame className="w-3.5 h-3.5 mr-1" />Fire Safety</TabsTrigger>
          <TabsTrigger value="coshh"><FlaskConical className="w-3.5 h-3.5 mr-1" />COSHH</TabsTrigger>
        </TabsList>

        <TabsContent value="pubinfo">
          <VenuePubInfoForm info={data?.info ?? null} onSaved={refresh} />
        </TabsContent>
        <TabsContent value="qa">
          <QAManageTab items={data?.qaItems ?? []} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="rooms">
          <VenueRoomsTab rooms={data?.rooms ?? []} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="tables">
          <VenueTablesTab tables={data?.tables ?? []} rooms={data?.rooms ?? []} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="planner">
          <TablePlannerCanvas />
        </TabsContent>
        <TabsContent value="firesafety">
          <FireSafetyAdminTab rooms={rooms} />
        </TabsContent>
        <TabsContent value="coshh">
          <CoshhAdminTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
